import { useEffect, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useLazyQuery } from '@apollo/client';
import { Button, Box, Grid } from '@mui/material';
import { StatelessSelector, Pill } from '@care/react-component-lib';
import * as queryString from 'querystring';
import { FormikProvider, useFormik } from 'formik';
import {
  CLIENT_FEATURE_FLAGS,
  SEEKER_DAYCARE_CHILD_CARE_ROUTES,
  FLOWS,
  SEEKER_CHILD_CARE_ROUTES,
  SEM_REMOVE_DAYCARE_OPTION_UTM_CODE,
  SKIP_AUTH_CONTEXT_KEY,
  BUSINESS_SEARCH_MFE_PATH,
} from '@/constants';
import {
  AdditionalCareKind,
  CareKind,
  CareKindLabels,
  DayCareKindLabels,
  DefaultCareKind,
  ServiceIdsForMember,
} from '@/types/seekerCC';
import { AnalyticsHelper } from '@/utilities/analyticsHelper';
import { logChildCareEvent } from '@/utilities/childCareAnalyticsHelper';
import Header from '@/components/Header';
import {
  useAppDispatch,
  useSeekerState,
  useSeekerCCState,
  useFlowState,
} from '@/components/AppState';
import useFlowNavigation from '@/components/features/flowNavigation/useFlowNavigation';
import { TravelDistance } from '@/components/shared/TravelDistance/TravelDistance';
import { useFeatureFlags } from '@/components/FeatureFlagsContext';
import useEnterKey from '@/components/hooks/useEnterKey';
import { useCommonSharedData } from '@/components/hooks/useCommonSharedData';
import useIsEarlyPremiumIntro from '@/components/hooks/useIsEarlyPremiumIntro';
import { useChildcareDaycareBackup } from '@/components/hooks/useChildcareDaycareBackup';
import { SxClassProps } from '@/types/common';
import useIsSeekerEnrollment2 from '@/components/hooks/useIsSeekerEnrollment2';
import { ServiceType, DistanceUnit } from '@/__generated__/globalTypes';
import { isValidZipCode } from '@/utilities/globalValidations';
import { getBusinessSearchURLString } from '@/utilities/daycareHelper';
import { getZipcodeSummary, getZipcodeSummaryVariables } from '@/__generated__/getZipcodeSummary';
import { GET_ZIP_CODE_SUMMARY } from '@/components/request/GQL';
import { useRetrieveVhpMfeSharedData } from '@/components/hooks/useVhpMfeSharedData';
import getPills from './pillsOptions';
import CareKindRebrand from './care-kind-rebrand';

const classes: SxClassProps = {
  selector: (theme) => ({
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(4),
    },
    '& .MuiListItem-root': {
      marginBottom: theme.spacing(0),
      padding: theme.spacing(theme.spacing(1), 0),

      [theme.breakpoints.up('md')]: {
        marginBottom: theme.spacing(-0.5),
      },

      '& > label': {
        height: 'auto',
        padding: theme.spacing(2, 1, 2, 2.5),
      },
    },
  }),
  mainContainer: (theme) => ({
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  }),
};

const getOptionLabel = (optionsArray: any[], targetName: string) => {
  const seekOption = optionsArray.find((option: any) => option.value === targetName);
  return seekOption ? seekOption.label : targetName;
};

function CareKindPage() {
  const dispatch = useAppDispatch();
  const { goNext } = useFlowNavigation();
  const router = useRouter();
  const { fromSeoPage, fromSemPage } = router.query;
  const featureFlags = useFeatureFlags();
  const [skipLocation, setSkipLocation] = useState<boolean>(false);
  const { flowName, utmCampaign, referrerCategory } = useFlowState();
  const { maxDistanceFromSeekerDayCare, zipcode, city, state } = useSeekerState(); // Remove as part of GROW-187
  const { careKind, enrollmentSource, czenServiceIdForMember, careDate } = useSeekerCCState();
  const { data: vhpSharedData, isFetching: isFetchingVhpData } = useRetrieveVhpMfeSharedData();
  const { publishCommonSharedData } = useCommonSharedData();
  const { isChildcareBackupCare } = useChildcareDaycareBackup();
  const { isSeekerEnrollment2Eligible } = useIsSeekerEnrollment2(ServiceType.CHILD_CARE);
  const { daycareEarlyPremiumIntroFlag } = useIsEarlyPremiumIntro();

  const shouldShowDayCareOptions = czenServiceIdForMember === 'dayCare';

  const seekerDirectoryDCFlag =
    featureFlags?.flags[CLIENT_FEATURE_FLAGS.GROWTH_ENROLLMENT_SEEKER_DIRECTORY_DC];
  const shouldRedirectToSeekerDirectoryDC = seekerDirectoryDCFlag?.value === 2;

  const shouldUseDaycareEnrollmentFlow =
    featureFlags.flags[CLIENT_FEATURE_FLAGS.PN_DAYCARE_ENROLLMENT_FLOW]?.value;

  const removeDaycareOption =
    featureFlags?.flags[CLIENT_FEATURE_FLAGS.GROWTH_ENROLLMENT_CC_REMOVE_DC_SEM_GENERIC_CAMPAIGN];
  const isRemoveDaycareOptionEligible =
    utmCampaign === SEM_REMOVE_DAYCARE_OPTION_UTM_CODE && !shouldShowDayCareOptions;
  const isRemoveDaycareOption = isRemoveDaycareOptionEligible && removeDaycareOption?.value === 2;

  const options = getPills({
    shouldShowDayCareOptions,
    isRemoveDaycareOption,
  });

  // https://carecom.atlassian.net/browse/PN-5779: daycare-enrollment-reduced-v2 A/B test
  // index: 0 - reduced flow off - value = 0
  // index: 1 - reduced flow on - value = 1
  const reducedDaycareFlag = featureFlags.flags[CLIENT_FEATURE_FLAGS.DAYCARE_REDUCED_FUNNEL_V2];
  const inReducedDaycareFunnel = reducedDaycareFlag?.value === 1;

  const shouldShowSeekerMultiVertical =
    featureFlags?.flags[CLIENT_FEATURE_FLAGS.GROWTH_CAPTURE_MULTI_VERTICAL]?.variationIndex === 2;

  // A/B test to display distance willing to travel for Preschool & Afterschool care too
  const travelDistanceFlag =
    featureFlags?.flags[CLIENT_FEATURE_FLAGS.DAYCARE_DISTANCE_TRAVELED_SELECTION];
  const showTravelDistanceControl =
    (careKind === DefaultCareKind.DAY_CARE_CENTERS ||
      (travelDistanceFlag?.value === 'distance' && shouldShowDayCareOptions)) &&
    !isSeekerEnrollment2Eligible;

  const resetFlowName = (kindOfCare: CareKind) => {
    let flow = FLOWS.SEEKER_CHILD_CARE.name;
    const isDayCare = kindOfCare === DefaultCareKind.DAY_CARE_CENTERS;

    if (isSeekerEnrollment2Eligible) {
      flow = isDayCare
        ? FLOWS.SEEKER_DAYCARE_CHILD_CARE_REDESIGN.name
        : FLOWS.SEEKER_CHILD_CARE_REDESIGN.name;
    } else if (isDayCare) {
      flow = !inReducedDaycareFunnel
        ? FLOWS.SEEKER_DAYCARE_CHILD_CARE.name
        : FLOWS.SEEKER_DAYCARE_CHILD_CARE_REDUCED.name;
    } else if (shouldShowSeekerMultiVertical) {
      if (flow === FLOWS.SEEKER_CHILD_CARE.name) {
        flow = FLOWS.SEEKER_CHILD_CARE_WITH_ADD_ON_VERTICALS.name;
      }
    }

    dispatch({ type: 'setFlowName', flowName: flow });
  };

  const [getZipCodeSummary] = useLazyQuery<getZipcodeSummary, getZipcodeSummaryVariables>(
    GET_ZIP_CODE_SUMMARY,
    {
      context: { [SKIP_AUTH_CONTEXT_KEY]: true },
      onCompleted: ({ getZipcodeSummary: { __typename } }) => {
        if (__typename === 'ZipcodeSummary') {
          setSkipLocation(true);
        } else {
          /* istanbul ignore next */
          setSkipLocation(false);
        }
      },
      onError: () => {
        /* istanbul ignore next */
        setSkipLocation(false);
      },
    }
  );

  useEffect(() => {
    if (isRemoveDaycareOptionEligible) {
      AnalyticsHelper.logTestExposure(
        CLIENT_FEATURE_FLAGS.GROWTH_ENROLLMENT_CC_REMOVE_DC_SEM_GENERIC_CAMPAIGN,
        removeDaycareOption
      );
    }
  }, []);

  useEffect(() => {
    resetFlowName(careKind);

    if (shouldShowDayCareOptions) {
      AnalyticsHelper.logTestExposure(
        CLIENT_FEATURE_FLAGS.DAYCARE_DISTANCE_TRAVELED_SELECTION,
        travelDistanceFlag
      );
    }
  }, []);

  useEffect(() => {
    publishCommonSharedData();
  }, [careKind]);

  useEffect(() => {
    if (
      isSeekerEnrollment2Eligible &&
      (czenServiceIdForMember === ServiceIdsForMember.nanny ||
        czenServiceIdForMember === ServiceIdsForMember.babysitter) &&
      (fromSeoPage || fromSemPage)
    ) {
      dispatch({
        type: 'setCareKind',
        careKind:
          czenServiceIdForMember === ServiceIdsForMember.nanny
            ? DefaultCareKind.NANNIES_RECURRING_BABYSITTERS
            : DefaultCareKind.ONE_TIME_BABYSITTERS,
      });
    }
  }, []);

  useEffect(() => {
    if (isSeekerEnrollment2Eligible && (fromSeoPage || fromSemPage) && isValidZipCode(zipcode)) {
      getZipCodeSummary({
        variables: {
          zipcode,
        },
      });
    } else {
      setSkipLocation(false);
    }
  }, []);

  useEffect(() => {
    if (isSeekerEnrollment2Eligible) {
      dispatch({
        type: 'setMaxDistanceFromSeekerDayCare',
        distance: 10,
        unit: DistanceUnit.MILES,
      });
    }
  }, []);

  const disableNext = !careKind;

  const daycareDistanceFromState = useSeekerState().maxDistanceFromSeekerDayCare.distance;

  const pushToDCEnrollment = () => {
    const newQuery = {
      funnelData: JSON.stringify({
        zipcode,
        city,
        state,
        startDate: careDate === 'JUST_BROWSING' ? `planning ahead` : careDate,
        travelDistance: daycareDistanceFromState,
      }),
    };
    const redirectUrl = `${
      window.location.origin
    }/app/daycare-enrollment/children?${queryString.stringify(newQuery)}`;
    window.location.assign(redirectUrl);
  };

  const handleSubmission = () => {
    // Analytics
    let optionsToLog: Record<string, any> = {};
    if (careKind === DefaultCareKind.DAY_CARE_CENTERS || shouldShowDayCareOptions) {
      // beginning of the reduced daycare enrollment funnel test, expose
      AnalyticsHelper.logTestExposure(
        CLIENT_FEATURE_FLAGS.DAYCARE_REDUCED_FUNNEL_V2,
        reducedDaycareFlag
      );

      AnalyticsHelper.logTestExposure(
        CLIENT_FEATURE_FLAGS.DAYCARE_ENROLLMENT_EARLY_PREMIUM_INTRO_FLOW,
        daycareEarlyPremiumIntroFlag
      );

      if (isSeekerEnrollment2Eligible) {
        AnalyticsHelper.logTestExposure(
          CLIENT_FEATURE_FLAGS.GROWTH_ENROLLMENT_SEEKER_DIRECTORY_DC,
          seekerDirectoryDCFlag
        );
      }
    }

    if (shouldShowDayCareOptions) {
      optionsToLog = {
        subvertical: DayCareKindLabels.DAY_CARE_CENTERS,
        user_choice: getOptionLabel(options, careKind),
      };
    } else {
      optionsToLog = {
        subvertical: getOptionLabel(options, careKind),
      };
    }

    if (showTravelDistanceControl) {
      optionsToLog = {
        ...optionsToLog,
        extraData: {
          user_distance: maxDistanceFromSeekerDayCare.distance,
          user_distance_unit: maxDistanceFromSeekerDayCare.unit,
        },
      };
    }

    if (isChildcareBackupCare && careKind === AdditionalCareKind.CHILDCARE_DAYCARE_BACKUP) {
      optionsToLog = {
        ...optionsToLog,
        subvertical: CareKindLabels.CHILDCARE_DAYCARE_BACKUP,
      };
    }

    if (isSeekerEnrollment2Eligible) {
      optionsToLog = {
        ...optionsToLog,
        screen_name: 'Care Kind',
        subVertical: careKind,
        subvertical_text: getOptionLabel(options, careKind),
        referer: document.referrer,
      };

      if (careKind === DefaultCareKind.DAY_CARE_CENTERS) {
        optionsToLog.extraData = {
          user_distance: maxDistanceFromSeekerDayCare.distance,
          user_distance_unit: maxDistanceFromSeekerDayCare.unit,
        };
      }

      delete optionsToLog.subvertical;
    }

    logChildCareEvent(
      'Member Enrolled - Seeker',
      'Care Kind',
      enrollmentSource,
      optionsToLog,
      'Next'
    );

    // End Analytics
    const skipNextStep = router.pathname === SEEKER_CHILD_CARE_ROUTES.INDEX;
    const hasVhpZipcode = !isFetchingVhpData && vhpSharedData?.zipcodeData?.zipcode;
    const shouldSkipLocationForEnrollment2 = Boolean(hasVhpZipcode || skipLocation);

    if (careKind === DefaultCareKind.DAY_CARE_CENTERS || shouldShowDayCareOptions) {
      if (isSeekerEnrollment2Eligible) {
        if (shouldRedirectToSeekerDirectoryDC) {
          const urlString = getBusinessSearchURLString(
            {
              zipcode,
              referrerCategory,
            },
            true
          );

          window.location.href = BUSINESS_SEARCH_MFE_PATH(urlString);
        } else {
          goNext(shouldSkipLocationForEnrollment2);
        }
      } else if (shouldUseDaycareEnrollmentFlow) {
        pushToDCEnrollment();
      } else if ([FLOWS.SEEKER_DAYCARE_CARE_KIND.name].includes(flowName)) {
        goNext(skipNextStep);
      } else {
        const nextStep = SEEKER_DAYCARE_CHILD_CARE_ROUTES.WHO;
        router.push(nextStep);
      }
    } else if (isSeekerEnrollment2Eligible) {
      goNext(shouldSkipLocationForEnrollment2);
    } else {
      goNext(skipNextStep);
    }
  };

  const onChangeHandler = (value: string[]) => {
    if (!value.length) return;
    const selection = value[0];

    dispatch({ type: 'setCareKind', careKind: selection as CareKind });

    resetFlowName(selection as CareKind);
  };

  const formik = useFormik<{ serviceTypes: string }>({
    initialValues: {
      serviceTypes: '',
    },
    onSubmit: handleSubmission,
  });

  const handleEnterKey = () => {
    formik.submitForm();
  };

  useEnterKey(true, () => handleEnterKey(), true);

  if (isSeekerEnrollment2Eligible) {
    return <CareKindRebrand formik={formik} onChangeHandler={onChangeHandler} />;
  }

  return (
    <FormikProvider value={formik}>
      <Head>
        <title>What kind of care?</title>
      </Head>
      <Grid container sx={classes.mainContainer}>
        <Grid item xs={12}>
          <Header variant="h2">What kind of care?</Header>
        </Grid>
        <Grid item xs={12}>
          <span data-hj-ignore-attributes>
            <StatelessSelector
              single
              name="careKind"
              onChange={onChangeHandler}
              sx={classes.selector}>
              {options.map((option) => (
                <Pill
                  key={option.value}
                  size="lg"
                  label={option.label}
                  value={option.value}
                  selected={careKind === option.value}
                  description={option.description}
                  unitId={option.value}
                />
              ))}
            </StatelessSelector>
          </span>
        </Grid>
        {showTravelDistanceControl && (
          <Box mt={3}>
            <TravelDistance />
          </Box>
        )}
        <Grid item xs={12} py={showTravelDistanceControl ? 1 : 3}>
          <Button
            onClick={formik.submitForm}
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            disabled={disableNext}>
            Next
          </Button>
        </Grid>
      </Grid>
    </FormikProvider>
  );
}

export default CareKindPage;
